<template>
  <div class="receivables-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">ACCOUNTS RECEIVABLE</p>
        <h1>入金・売掛管理</h1>
        <p>
          契約企業ごとに、<strong>いくら未収で、それがいつの月のぶんか</strong>を並べます。
          請求管理はその月だけを見る画面なので、
          古い月のぶんが残ったまま新しい月だけ払われている、という状態は見つけられません。
        </p>
      </div>
      <div class="header-actions">
        <CSelect
          :value.sync="monthSpan"
          :options="spanOptions"
          :disabled="loading"
          class="span-input"
          @update:value="load"
        />
        <CButton
          color="secondary"
          variant="outline"
          :disabled="loading"
          @click="load"
        >
          再読込
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>

    <!--
      **いちばん上は「次に何をするか」で並べる。**

      金額の合計だけを大きく出しても、運営は次の行動を決められない。
      猶予中なら電話、見るだけなら支払いの予定を聞く、停止中なら
      止めた旨を伝える、と手当てが違う。
    -->
    <div class="stage-cards">
      <button
        v-for="card in stageCards"
        :key="card.stage"
        type="button"
        class="stage-card"
        :class="[card.stage.toLowerCase(), { on: stageFilter === card.stage }]"
        @click="toggleStage(card.stage)"
      >
        <span class="stage-name">{{ card.label }}</span>
        <span class="stage-count">{{ card.count }} 社</span>
        <span class="stage-amount">{{ yen(card.balance) }}</span>
        <span class="stage-todo">{{ card.todo }}</span>
      </button>
    </div>

    <!--
      **払ってもらえない理由が、こちら側にあることがある。**
      請求書をまだ出していなければ、相手は払いようがない。
      相手のせいと自分のせいを混ぜない。
    -->
    <CAlert v-if="notIssuedAccountCount > 0" color="warning">
      <strong>{{ notIssuedAccountCount }} 社は、請求書をまだ発行していません。</strong>
      発行していない請求は相手に届いていないので、
      催促する前に請求管理から発行してください。
    </CAlert>

    <div class="toolbar">
      <CInput
        v-model="keyword"
        placeholder="会社名で絞り込む"
        class="keyword-input"
      />
      <label class="only-unpaid">
        <input v-model="onlyUnpaid" type="checkbox">
        未収のある会社だけ
      </label>
      <span v-if="stageFilter" class="filter-chip">
        {{ stageLabelOf(stageFilter) }}だけ
        <button type="button" @click="stageFilter = ''">×</button>
      </span>
      <span class="total">
        未収の合計 <strong>{{ yen(balanceTotal) }}</strong>
      </span>
    </div>

    <!--
      **会社 × 月の表。**古い月が左、今月が右。
      横に読むと、その会社がどこから払えていないかが分かる。
    -->
    <div ref="agingScroll" class="aging-scroll">
      <table class="aging-table">
        <thead>
          <tr>
            <!--
              **会社名・未収の合計・いまの状態は1つの列にまとめる。**

              月を12か月ぶん並べると横に流れる。列を分けると、
              今月を見るために右へ寄せたとたん、**いちばん見たい
              未収の合計と状態が左へ消える**（2026-09-18、実機で発生）。
              固定できる列は1つだけなので、3つを1つに入れる。
            -->
            <th class="sticky-name">契約企業と未収の合計</th>
            <th
              v-for="month in months"
              :key="month"
              class="numeric month-head"
            >
              {{ readableMonth(month) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in visibleRows" :key="row.accountId">
            <td class="sticky-name">
              <div class="account-name">{{ row.accountName }}</div>
              <div class="account-id">ID {{ row.accountId }}</div>
              <div class="account-summary">
                <strong :class="{ owing: Number(row.balanceTotal) > 0 }">
                  {{ yen(row.balanceTotal) }}
                </strong>
                <CBadge :color="stageColor(row.worstStage)">
                  {{ row.worstStageLabel }}
                </CBadge>
              </div>
              <div v-if="row.oldestUnpaidMonth" class="oldest">
                {{ readableMonth(row.oldestUnpaidMonth) }}分から残っています
              </div>
            </td>
            <td
              v-for="month in months"
              :key="month"
              class="numeric cell"
              :class="cellClass(row, month)"
            >
              <template v-if="cellOf(row, month)">
                <div class="cell-balance">
                  {{ yen(cellOf(row, month).balanceAmount) }}
                </div>
                <div class="cell-note">
                  請求 {{ yen(cellOf(row, month).billedAmount) }}
                </div>
                <div
                  v-if="cellOf(row, month).notIssued"
                  class="cell-note warn"
                >
                  未発行
                </div>
                <div
                  v-else-if="cellOf(row, month).overdueDays > 0"
                  class="cell-note warn"
                >
                  {{ cellOf(row, month).overdueDays }}日超過
                </div>
              </template>
              <span v-else class="muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p v-if="!loading && !visibleRows.length" class="empty">
      <template v-if="accounts.length">
        絞り込みに当てはまる会社がありません。
      </template>
      <template v-else>
        この期間に確定した請求がまだありません。
        <strong>確定は毎月1日に自動で走ります。</strong>
      </template>
    </p>

    <!--
      **数字の出どころを書いておく。**運営は監査でこの表を出すことが
      ある。何を数えて何を外したのかが分からない表は使えない。
    -->
    <dl class="notes">
      <div>
        <dt>数えているもの</dt>
        <dd>確定した請求（見込みのままの請求は入れません）から、記録済みの入金を引いた額です。</dd>
      </div>
      <div>
        <dt>数えていないもの</dt>
        <dd>取り消した請求は入れません。取り消しは「その請求は無かった」という意味だからです。</dd>
      </div>
      <div>
        <dt>状態の区切り</dt>
        <dd>
          支払期限を過ぎると「猶予中」、そこから14日で「見るだけ」、
          さらに14日で「停止中」になります。<strong>この日付は請求を確定したときに決まります。</strong>
          あとから猶予の日数を変えても、すでにある請求の扱いは変わりません。
        </dd>
      </div>
    </dl>
  </div>
</template>

<script>
/**
 * 入金・売掛管理（会社 × 月）。
 *
 * ## なぜ作ったか
 *
 * 請求を作るところと入金を消し込むところまでは作ってあったのに、
 * **会社ごとにいま合計いくら未収で、それがいつの月のぶんなのか**を
 * 出す場所がどこにも無かった。請求管理はその月だけを見る画面なので、
 * 月を1つずつ切り替えて目で足すしかなかった。
 *
 * ## 何をまねたか
 *
 * 実務の債権管理で使う「売掛金年齢表」。請求先ごとの残高を月ごとに
 * 区分して並べ、支払期日からどれだけ経ったかを見る。
 *
 * ## 区分は「次に何をするか」で切る
 *
 * 一般的な年齢表は 30日超・60日超・90日超 で刻む。**それをそのまま
 * 使うと、画面の区分と製品の動きが食い違う。**PREIKA が読み取り
 * 専用へ落とすのは支払期限＋14日、止めるのはさらに＋14日なので、
 * 「まだ30日超だから電話だけでいい」と思っている時点で相手は
 * もう止まっている。区分は実際に手当てが変わる日で切る。
 *
 * ## どこから見えるか
 *
 * **運営メニュー →「入金・売掛管理」。**
 */
import providerBilling from '@/services/providerBilling'

/** 区分ごとに、運営が次にすること。**区分だけ出しても行動が決まらない。** */
const STAGE_TODO = {
  WITHIN_DUE: '入金を待つ',
  IN_GRACE: '電話・メールで確認する',
  READ_ONLY: '支払いの予定を聞く',
  SUSPENDED: '止めた旨を伝える'
}

const STAGE_LABEL = {
  WITHIN_DUE: '期限内',
  IN_GRACE: '猶予中',
  READ_ONLY: '見るだけ',
  SUSPENDED: '停止中'
}

const STAGE_COLOR = {
  WITHIN_DUE: 'secondary',
  IN_GRACE: 'warning',
  READ_ONLY: 'danger',
  SUSPENDED: 'dark'
}

/** 区分の並び。**重い順に右へ**ではなく、時間の流れの順に左から。 */
const STAGE_ORDER = ['WITHIN_DUE', 'IN_GRACE', 'READ_ONLY', 'SUSPENDED']

export default {
  name: 'PlatformReceivables',

  data () {
    return {
      loading: false,
      errorMessage: '',
      /*
       * **既定は6か月。**
       *
       * 12か月ぶん並べると、画面の幅に収まらず左端の列が途中で
       * 切れる。「2025年10月」の右半分だけが残って「10月」と読め、
       * **今年の10月と読み違える**（2026-09-18、実機で発生）。
       * 滞留は90日を超えると回収率が落ちるので、ふだん見るのは
       * 直近の数か月。古いところは必要なときに広げる。
       */
      monthSpan: 6,
      spanOptions: [
        { value: 6, label: '6か月' },
        { value: 12, label: '12か月' },
        { value: 24, label: '24か月' }
      ],
      months: [],
      accounts: [],
      summary: {},
      keyword: '',
      onlyUnpaid: true,
      stageFilter: ''
    }
  },

  computed: {
    balanceTotal () {
      return Number(this.summary.balanceTotal || 0)
    },

    notIssuedAccountCount () {
      return Number(this.summary.notIssuedAccountCount || 0)
    },

    stageCards () {
      const counts = this.summary.countByStage || {}
      const balances = this.summary.balanceByStage || {}
      return STAGE_ORDER.map(stage => ({
        stage,
        label: STAGE_LABEL[stage],
        todo: STAGE_TODO[stage],
        count: Number(counts[stage] || 0),
        balance: Number(balances[stage] || 0)
      }))
    },

    visibleRows () {
      const word = String(this.keyword || '').trim()
      return this.accounts.filter(row => {
        if (this.onlyUnpaid && Number(row.balanceTotal) <= 0) return false
        if (this.stageFilter) {
          if (Number(row.balanceTotal) <= 0) return false
          if (row.worstStage !== this.stageFilter) return false
        }
        if (word && String(row.accountName || '').indexOf(word) < 0) {
          return false
        }
        return true
      })
    }
  },

  created () {
    this.load()
  },

  methods: {
    load () {
      this.loading = true
      this.errorMessage = ''
      return providerBilling
        .receivables(this.monthSpan)
        .then(data => {
          this.months = (data && data.months) || []
          this.accounts = (data && data.accounts) || []
          this.summary = (data && data.summary) || {}
          this.showLatestMonth()
        })
        .catch(error => {
          this.errorMessage = this.readError(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    /*
     * **開いたら今月が見えている状態にする。**
     *
     * 年齢表は古い月が左、今月が右。12か月ぶん並べると今月が
     * 画面の外へ出るので、**いちばん知りたい今月と先月が
     * 最初は見えない。**開いた直後に右端まで寄せる。
     * 古い月は左へ戻せば見える（古いほうは探しに行く動きでよい）。
     */
    showLatestMonth () {
      this.$nextTick(() => {
        const box = this.$refs.agingScroll
        if (!box) return
        box.scrollLeft = box.scrollWidth
      })
    },

    /*
     * **押した区分だけに絞る。**もう一度押すと戻す。
     * 絞り込んだことが分かるように、上の帯にも出す。
     */
    toggleStage (stage) {
      this.stageFilter = this.stageFilter === stage ? '' : stage
    },

    cellOf (row, month) {
      return row.months ? row.months[month] : null
    },

    cellClass (row, month) {
      const cell = this.cellOf(row, month)
      if (!cell) return ''
      if (Number(cell.balanceAmount) <= 0) return 'settled'
      return cell.stage ? cell.stage.toLowerCase() : ''
    },

    stageLabelOf (stage) {
      return STAGE_LABEL[stage] || stage
    },

    stageColor (stage) {
      return STAGE_COLOR[stage] || 'secondary'
    },

    readableMonth (yearMonth) {
      const text = String(yearMonth || '')
      if (text.length !== 6) return text
      return text.slice(0, 4) + '年' + Number(text.slice(4, 6)) + '月'
    },

    yen (value) {
      return '¥' + Number(value || 0).toLocaleString('ja-JP')
    },

    readError (error) {
      const data = error && error.response && error.response.data
      if (data && data.message) return data.message
      return '入金・売掛管理を読み込めませんでした。時間をおいて画面を再読み込みしてください。'
    }
  }
}
</script>

<style scoped>
.receivables-page { padding-bottom: 2rem; }

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.2rem;
}
.page-header h1 { font-size: 1.5rem; margin: 0 0 .4rem; }
.page-header p { margin: 0; max-width: 46rem; color: #4f5d6b; }
.eyebrow {
  font-size: .75rem;
  letter-spacing: .16em;
  color: #8a97a4;
  margin: 0 0 .15rem;
}
.header-actions { display: flex; gap: .5rem; align-items: center; }
.span-input { margin-bottom: 0; min-width: 8rem; }

/* 区分ごとの札。押すとその区分だけに絞る。 */
.stage-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: .7rem;
  margin-bottom: 1rem;
}
.stage-card {
  display: flex;
  flex-direction: column;
  gap: .15rem;
  text-align: left;
  padding: .7rem .85rem;
  border: 1px solid #d8dde3;
  border-left-width: 4px;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}
.stage-card.on { box-shadow: 0 0 0 2px rgba(50, 110, 180, .35); }
.stage-card.within_due { border-left-color: #b6bec7; }
.stage-card.in_grace { border-left-color: #e0a44b; }
.stage-card.read_only { border-left-color: #d9534f; }
.stage-card.suspended { border-left-color: #4a4a4a; }
.stage-name { font-weight: 700; font-size: .92rem; }
.stage-count { font-size: .82rem; color: #6b7783; }
.stage-amount { font-size: 1.05rem; font-weight: 700; }
.stage-todo { font-size: .78rem; color: #6b7783; }

.toolbar {
  display: flex;
  align-items: center;
  gap: .8rem;
  flex-wrap: wrap;
  margin-bottom: .6rem;
}
.keyword-input { margin-bottom: 0; max-width: 16rem; }
.only-unpaid { margin: 0; font-size: .9rem; display: flex; gap: .35rem; }
.filter-chip {
  font-size: .82rem;
  background: #eef2f6;
  border-radius: 999px;
  padding: .15rem .6rem;
}
.filter-chip button {
  border: 0;
  background: none;
  cursor: pointer;
  padding: 0 0 0 .3rem;
}
.total { margin-left: auto; font-size: .92rem; }

/* 月が増えると横に伸びる。表だけを横に流す。 */
.aging-scroll { overflow-x: auto; }
.aging-table { width: 100%; border-collapse: collapse; font-size: .86rem; }
.aging-table th,
.aging-table td {
  border: 1px solid #e3e7eb;
  padding: .4rem .55rem;
  vertical-align: top;
}
.aging-table thead th { background: #f4f6f8; white-space: nowrap; }
.month-head { min-width: 7.5rem; }
.numeric { text-align: right; }

/* 会社名は横に流れても残す。どの行を見ているか分からなくなる。 */
.sticky-name {
  position: sticky;
  left: 0;
  background: #fff;
  text-align: left;
  min-width: 15rem;
  z-index: 1;
  /* 右へ流れたときに、月の数字と重なって読めなくならないように。 */
  box-shadow: 1px 0 0 #e3e7eb;
}
.account-summary {
  display: flex;
  align-items: center;
  gap: .4rem;
  margin-top: .15rem;
}
.account-summary strong { font-size: 1rem; }
/*
  **見出しの固定列は、本文より上に置く。**
  同じ z-index だと、あとから描かれる隣の月の見出しが上に乗って、
  固定した見出しの右端に月名が食い込む（2026-09-18、実機で発生）。
*/
.aging-table thead .sticky-name { background: #f4f6f8; z-index: 2; }
.account-name { font-weight: 600; }
.account-id { font-size: .75rem; color: #8a97a4; }
.oldest { font-size: .75rem; color: #a05a1e; }
.owing { color: #b3413d; }

.cell.settled { background: #f6faf6; color: #7b8a7b; }
.cell.in_grace { background: #fdf5e9; }
.cell.read_only { background: #fdeceb; }
.cell.suspended { background: #eceef0; }
.cell-balance { font-weight: 600; }
.cell-note { font-size: .75rem; color: #7c8794; }
.cell-note.warn { color: #b3413d; }
.muted { color: #b6bec7; }

.empty { margin-top: 1rem; color: #6b7783; }

.notes {
  margin-top: 1.4rem;
  padding-top: .9rem;
  border-top: 1px solid #e3e7eb;
  font-size: .84rem;
  color: #6b7783;
}
.notes div { margin-bottom: .4rem; }
.notes dt { font-weight: 700; color: #4f5d6b; }
.notes dd { margin: 0 0 0 1rem; }

/*
  スマホでは札が縦に積まれ、表は横に流す。
  見出しの行だけ残しても、金額が読めなければ意味が無い。
*/
@media (max-width: 575.98px) {
  .page-header { flex-direction: column; }
  .header-actions { width: 100%; }
  .total { margin-left: 0; }
  .sticky-name { min-width: 9rem; }
  .aging-table { font-size: .8rem; }
}
</style>
